import type { InvestmentFundRecord } from '@/types/fund';
import { toDomainFund } from './fundAdapter';

export type FundStatus =
  | '筹备中'
  | '募集期'
  | '投资期'
  | '退出期'
  | '延长期'
  | '清算中'
  | '清算完成';

export type OpportunityStage =
  | '潜在目标'
  | '初步接洽'
  | '方案沟通'
  | '尽调评审'
  | '内部决策'
  | '签约认缴'
  | '实缴推进'
  | '完成'
  | '暂缓'
  | '失败';

export interface Fund {
  id: string;
  /** 后端基金代码（真实 API 接入后存在）。 */
  code?: string;
  currency?: string;
  name: string;
  shortName: string;
  type: string;
  registrationType?: string;
  organizationForm?: string;
  generalPartnerUuid?: string | null;
  manager: string;
  owner: string;
  target: number;
  status: FundStatus;
  establishedAt: string;
  updatedAt: string;
  duration: string;
  region: string;
  strategy: string;
  industries: string[];
  minimum: number;
  firstCloseTarget: number;
  firstCloseAt: string;
  finalCloseAt: string;
  controlType?: '自管' | '参股';
  ourShareRatio?: number;
  isAbsoluteControl?: boolean;
  controlRemark?: string;
  /** 基金性质：盲池 / 产业 / 直投（CVC）。 */
  fundKind?: string;
  /** 托管人机构 UUID。 */
  custodianUuid?: string | null;
  /** 基金管理人机构 UUID（与 GP 可能不同，指持牌主体）。 */
  fundManagerUuid?: string | null;
  managerUnifiedSocialCreditCode?: string | null;
  managementEntity?: string | null;
  partnershipEntity?: string | null;
  partnershipOrganizationUuid?: string | null;
  exitPeriodEndAt?: string | null;
  strategyType?: string | null;
  registrationNumber?: string | null;
  registrationDate?: string | null;
  /** 出资主体机构 UUID（CVC/直投场景）。 */
  investmentSubjectUuid?: string | null;
  /** 管理平台机构 UUID；与出资主体同一实体时即为直投。 */
  managerPlatformUuid?: string | null;
  /** 基金状态（启用/停用），与 phase 正交。 */
  fundStatus?: '启用' | '停用';
}

export interface LpOrganization {
  id: string;
  name: string;
  type: string;
  region: string;
  contactId: string;
  owner: string;
  ownerUuid?: string | null;
  preferences: string[];
  relationStatus: '高潜力' | '跟进中' | '已建立关系' | '历史LP';
  lastContactAt: string;
  source: string;
  scale: string;
  introduction: string;
  unifiedSocialCreditCode?: string;
  qualificationAttachmentUuid?: string;
  bankName?: string;
  bankAccount?: string;
}

export interface Contact {
  id: string;
  lpId: string;
  name: string;
  title: string;
  department: string;
  role: string;
  phone: string;
  email: string;
  owner: string;
  relationStrength: '强' | '中' | '弱';
  lastContactAt: string;
}

export interface FundraisingOpportunity {
  id: string;
  name: string;
  lpId: string;
  fundId: string;
  owner: string;
  contactIds: string[];
  stage: OpportunityStage;
  expectedAmount: number;
  probability: number;
  nextAction: string;
  lastFollowUpAt: string;
  risk: '低风险' | '中风险' | '高风险';
  source: string;
  expectedDecisionAt: string;
  background: string;
  judgement: string;
  risks: string[];
}

export interface Commitment {
  evidenceStatus?: 'verified' | 'pending';
  id: string;
  opportunityId: string;
  fundId: string;
  lpId: string;
  amount: number;
  signedAt: string;
  /** 实际出资人（机构级穿透）。 */
  actualInvestor?: string | null;
}

export interface ContributionInstallment {
  id: string;
  name: string;
  dueAmount: number;
  dueAt: string;
  paidAmount: number;
  paidAt?: string;
  status: '待通知' | '待缴' | '部分到账' | '已到账' | '逾期';
}

export interface ContributionPlan {
  id: string;
  commitmentId: string;
  fundId: string;
  lpId: string;
  owner: string;
  installments: ContributionInstallment[];
}

export interface FollowUp {
  id: string;
  lpId: string;
  opportunityId?: string;
  contactId?: string;
  type: string;
  content: string;
  author: string;
  createdAt: string;
  nextAction?: string;
}

export interface Task {
  id: string;
  title: string;
  fundId?: string;
  lpId?: string;
  opportunityId?: string;
  owner: string;
  dueAt: string;
  priority: '高' | '中' | '低';
  status: '待处理' | '进行中' | '已完成';
}

export interface DueDiligenceQuestion {
  id: string;
  opportunityId: string;
  category: string;
  question: string;
  owner: string;
  status: '待回复' | '准备中' | '已回复' | '待补充' | '已确认';
  answer?: string;
}

export interface Activity {
  id: string;
  type: string;
  title: string;
  description: string;
  createdAt: string;
  entityType: 'fund' | 'lp' | 'opportunity' | 'contribution';
  entityId: string;
}

export interface FundraisingDomainState {
  version: 1;
  funds: Record<string, Fund>;
  lps: Record<string, LpOrganization>;
  contacts: Record<string, Contact>;
  opportunities: Record<string, FundraisingOpportunity>;
  commitments: Record<string, Commitment>;
  contributionPlans: Record<string, ContributionPlan>;
  followUps: Record<string, FollowUp>;
  tasks: Record<string, Task>;
  dueDiligenceQuestions: Record<string, DueDiligenceQuestion>;
  activities: Record<string, Activity>;
}

type RecordCommitmentPayload = Omit<Commitment, 'amount'> & { amount: number };

export type FundraisingCommand =
  | { type: 'reset' }
  | {
      type: 'hydrateFunds';
      /** 后端基金记录：替换 funds 字典，并按 code 映射重写 demo 实体的旧基金引用。 */
      payload: InvestmentFundRecord[];
    }
  | {
      type: 'hydrateCrm';
      /** 后端 CRM 数据：全量替换 lps / contacts / opportunities（不保留本地 seed）。 */
      payload: {
        lps: Record<string, any>;
        contacts: Record<string, any>;
        opportunities: Record<string, any>;
      };
    }
  | {
      type: 'hydrateContributions';
      /** 后端出资/认缴：全量替换 commitments 与 contributionPlans。 */
      payload: {
        commitments: Record<string, Commitment>;
        contributionPlans: Record<string, ContributionPlan>;
      };
    }
  | { type: 'createFund'; payload: Fund }
  | { type: 'updateFund'; payload: Partial<Fund> & Pick<Fund, 'id'> }
  | { type: 'createLp'; payload: LpOrganization }
  | {
      type: 'updateLp';
      payload: Partial<LpOrganization> & Pick<LpOrganization, 'id'>;
    }
  | { type: 'createContact'; payload: Contact }
  | { type: 'createOpportunity'; payload: FundraisingOpportunity }
  | {
      type: 'updateOpportunityStage';
      payload: { id: string; stage: OpportunityStage };
    }
  | { type: 'recordCommitment'; payload: RecordCommitmentPayload }
  | {
      type: 'recordContributionReceipt';
      payload: {
        planId: string;
        installmentId: string;
        amount: number;
        paidAt: string;
      };
    }
  | { type: 'createFollowUp'; payload: FollowUp }
  | { type: 'createTask'; payload: Task }
  | { type: 'completeTask'; payload: { id: string } };

const toRecord = <T extends { id: string }>(items: T[]): Record<string, T> =>
  Object.fromEntries(items.map((item) => [item.id, item]));

const mapValues = <T, R>(
  record: Record<string, T>,
  mapper: (item: T) => R,
): Record<string, R> => {
  const next: Record<string, R> = {};
  Object.entries(record).forEach(([key, item]) => {
    next[key] = mapper(item);
  });
  return next;
};

const funds: Fund[] = [
  [
    'fund-growth-1',
    '智造成长基金一期',
    '智造成长一期基金',
    '成长基金',
    '智造资本管理（北京）有限公司',
    '赵明涛',
    100000,
    '募集期',
    '2024-03-01',
    '2025-06-01',
    '10年（4年投资期 + 6年退出期）',
    '上海',
    '赋能型资本，助力企业产业化与全球化',
    ['高端制造', '工业软件', 'AI赋能', '机器人'],
    50000,
    30000,
    '2024-06-30',
    '2025-06-30',
  ],
  [
    'fund-industry-2',
    '先进制造产业基金二期',
    '先进制造二期',
    '产业基金',
    '先进制造资本',
    '李四',
    150000,
    '募集期',
    '2023-08-15',
    '2025-06-01',
    '10年',
    '苏州',
    '聚焦先进制造产业链关键环节',
    ['先进制造', '核心零部件'],
    80000,
    50000,
    '2024-04-30',
    '2025-12-31',
  ],
  [
    'fund-tech-3',
    '科技成果转化基金',
    '科创成果转化基金',
    '政府引导基金',
    '科创资本',
    '王五',
    120000,
    '募集期',
    '2023-09-10',
    '2025-05-31',
    '8年',
    '杭州',
    '高校科技成果转化与早期孵化',
    ['硬科技', '医疗健康'],
    60000,
    40000,
    '2024-02-28',
    '2025-09-30',
  ],
  [
    'fund-software',
    '工业软件专项基金',
    '工业软件基金',
    '专项基金',
    '智造资本',
    '赵六',
    80000,
    '投资期',
    '2022-11-05',
    '2025-05-30',
    '8年',
    '北京',
    '国产工业软件与智能制造基础设施',
    ['工业软件', 'SaaS'],
    40000,
    25000,
    '2023-06-30',
    '2024-12-31',
  ],
  [
    'fund-material',
    '新材料基金',
    '新材料基金',
    '产业基金',
    '新材资本',
    '孙七',
    100000,
    '募集期',
    '2024-01-20',
    '2025-05-29',
    '10年',
    '宁波',
    '关键材料与新能源产业链',
    ['新材料', '新能源'],
    50000,
    30000,
    '2024-08-31',
    '2026-03-31',
  ],
  [
    'fund-robot',
    '机器人基金',
    '机器人基金',
    '成长基金',
    '机器人产业资本',
    '周八',
    178000,
    '投资期',
    '2021-10-18',
    '2025-05-28',
    '10年',
    '深圳',
    '机器人本体、核心部件与场景应用',
    ['机器人', '智能硬件'],
    90000,
    60000,
    '2022-06-30',
    '2023-12-31',
  ],
].map((values) => {
  const [
    id,
    name,
    shortName,
    type,
    manager,
    owner,
    target,
    status,
    establishedAt,
    updatedAt,
    duration,
    region,
    strategy,
    industries,
    minimum,
    firstCloseTarget,
    firstCloseAt,
    finalCloseAt,
  ] = values as [
    string,
    string,
    string,
    string,
    string,
    string,
    number,
    FundStatus,
    string,
    string,
    string,
    string,
    string,
    string[],
    number,
    number,
    string,
    string,
  ];
  return {
    id,
    name,
    shortName,
    type,
    manager,
    owner,
    target,
    status,
    establishedAt,
    updatedAt,
    duration,
    region,
    strategy,
    industries,
    minimum,
    firstCloseTarget,
    firstCloseAt,
    finalCloseAt,
  };
});

const lpSeeds: Array<
  [
    string,
    string,
    string,
    string,
    string,
    string,
    string[],
    LpOrganization['relationStatus'],
    string,
    string,
  ]
> = [
  [
    'lp-ningbo',
    '宁波产业引导基金',
    '政府引导基金',
    '浙江·宁波',
    'contact-liuhaitao',
    '张伟',
    ['先进制造', '半导体', '科技创新'],
    '已建立关系',
    '2025-06-03',
    '某产业合作伙伴引荐',
  ],
  [
    'lp-huadong',
    '华东产业资本',
    '产业资本',
    '上海',
    'contact-chenxiaodong',
    '李明',
    ['高端制造', '新能源', '智能装备'],
    '跟进中',
    '2025-06-01',
    '产业活动',
  ],
  [
    'lp-jinmu',
    '金桥母基金',
    '母基金',
    '上海',
    'contact-liujia',
    '周舟',
    ['早期科技', '硬科技', '医疗健康'],
    '已建立关系',
    '2025-05-30',
    '历史合作',
  ],
  [
    'lp-listed',
    '某上市公司产业投资部',
    '产业资本',
    '广东·深圳',
    'contact-mowei',
    '张悦',
    ['产业链协同', 'SaaS', '工业互联网'],
    '高潜力',
    '2025-05-28',
    '项目合作',
  ],
  [
    'lp-family',
    '海岳家族办公室',
    '家族办公室',
    '北京',
    'contact-tang',
    '周舟',
    ['消费升级', '教育', '医疗健康'],
    '跟进中',
    '2025-05-26',
    '合作伙伴',
  ],
  [
    'lp-triangle',
    '长三角母基金',
    '母基金',
    '江苏·苏州',
    'contact-zhaoqiang',
    '李明',
    ['硬科技', '先进制造', '新能源'],
    '高潜力',
    '2025-05-24',
    '基金协会',
  ],
  [
    'lp-shanghai',
    '上海国资平台',
    '国资平台',
    '上海',
    'contact-liuyang',
    '周洋',
    ['科技创新', '成果转化'],
    '跟进中',
    '2025-06-02',
    '政府合作',
  ],
  [
    'lp-suzhou',
    '苏州工业园区引导基金',
    '政府引导基金',
    '江苏·苏州',
    'contact-zhaoqiang',
    '张三',
    ['智能制造', '返投项目'],
    '已建立关系',
    '2025-05-30',
    '园区合作',
  ],
  [
    'lp-shenzhen',
    '深圳市天使母基金',
    '母基金',
    '广东·深圳',
    'contact-sunwei',
    '李明',
    ['早期科技', '创业投资'],
    '已建立关系',
    '2025-05-28',
    '行业会议',
  ],
  [
    'lp-hefei',
    '合肥高新区产业基金',
    '政府引导基金',
    '安徽·合肥',
    'contact-xuxuan',
    '赵敏',
    ['新经济', '先进制造'],
    '跟进中',
    '2025-05-27',
    '招商合作',
  ],
];

const lps: LpOrganization[] = lpSeeds.map(
  ([
    id,
    name,
    type,
    region,
    contactId,
    owner,
    preferences,
    relationStatus,
    lastContactAt,
    source,
  ]) => ({
    id,
    name,
    type,
    region,
    contactId,
    owner,
    preferences,
    relationStatus,
    lastContactAt,
    source,
    scale: type === '母基金' ? '500.00 亿元' : '300.00 亿元',
    introduction: `${name}长期关注${preferences.join('、')}等领域，与本机构保持持续沟通。`,
  }),
);

for (let index = lps.length + 1; index <= 30; index += 1) {
  lps.push({
    id: `lp-${index}`,
    name: `${['启明', '星辰', '远航', '鼎新', '稳健'][index % 5]}资本${index}`,
    type: ['产业资本', '保险机构', '银行及理财子公司', 'FOF/MOM'][index % 4],
    region: ['北京', '上海', '深圳', '杭州'][index % 4],
    contactId: 'contact-generated-1',
    owner: ['李明', '王芳', '张伟', '刘洋'][index % 4],
    preferences: ['先进制造', '科技创新'],
    relationStatus: ['高潜力', '跟进中', '已建立关系'][
      index % 3
    ] as LpOrganization['relationStatus'],
    lastContactAt: `2025-05-${String((index % 20) + 1).padStart(2, '0')}`,
    source: '行业活动',
    scale: `${100 + index * 10}.00 亿元`,
    introduction: '长期机构投资者，关注科技创新和产业升级机会。',
  });
}

const contacts: Contact[] = [
  [
    'contact-liuhaitao',
    'lp-ningbo',
    '刘海涛',
    '投资部副总经理',
    '投资部',
    '核心影响人',
    '138-0000-8866',
    'liuhaitao@example.com',
    '张伟',
    '强',
    '2025-06-03',
  ],
  [
    'contact-chenxiaodong',
    'lp-huadong',
    '陈晓东',
    '投资总监',
    '战略投资部',
    '项目负责人',
    '138-0000-8898',
    'chen@example.com',
    '李明',
    '强',
    '2025-06-01',
  ],
  [
    'contact-liujia',
    'lp-jinmu',
    '刘佳',
    '投资经理',
    '基金投资部',
    '执行联系人',
    '138-0000-8012',
    'liujia@example.com',
    '周舟',
    '中',
    '2025-05-30',
  ],
  [
    'contact-mowei',
    'lp-listed',
    '莫伟',
    '总经理',
    '产业投资部',
    '最终决策人',
    '138-0000-8033',
    'mowei@example.com',
    '张悦',
    '中',
    '2025-05-28',
  ],
  [
    'contact-tang',
    'lp-family',
    '唐晓敏',
    '负责人',
    '投资管理部',
    '最终决策人',
    '138-0000-8055',
    'tang@example.com',
    '周舟',
    '中',
    '2025-05-26',
  ],
  [
    'contact-zhaoqiang',
    'lp-triangle',
    '赵强',
    '投资总监',
    '投资部',
    '核心影响人',
    '138-0000-8066',
    'zhao@example.com',
    '李明',
    '强',
    '2025-05-24',
  ],
  [
    'contact-liuyang',
    'lp-shanghai',
    '刘洋',
    '总经理助理',
    '基金管理部',
    '审批人',
    '138-0000-8077',
    'liu@example.com',
    '周洋',
    '中',
    '2025-06-02',
  ],
  [
    'contact-sunwei',
    'lp-shenzhen',
    '孙伟',
    '投资经理',
    '投资部',
    '项目负责人',
    '138-0000-8088',
    'sun@example.com',
    '李明',
    '强',
    '2025-05-28',
  ],
  [
    'contact-xuxuan',
    'lp-hefei',
    '徐刚',
    '投资经理',
    '基金管理部',
    '执行联系人',
    '138-0000-8099',
    'xu@example.com',
    '赵敏',
    '中',
    '2025-05-27',
  ],
  [
    'contact-generated-1',
    'lp-11',
    '王蕾',
    '投资经理',
    '投资部',
    '执行联系人',
    '138-0000-8100',
    'wanglei@example.com',
    '王芳',
    '中',
    '2025-05-20',
  ],
].map((item) => {
  const [
    id,
    lpId,
    name,
    title,
    department,
    role,
    phone,
    email,
    owner,
    relationStrength,
    lastContactAt,
  ] = item as [
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    Contact['relationStrength'],
    string,
  ];
  return {
    id,
    lpId,
    name,
    title,
    department,
    role,
    phone,
    email,
    owner,
    relationStrength,
    lastContactAt,
  };
});

for (let index = contacts.length + 1; index <= 50; index += 1) {
  const lp = lps[(index - 1) % lps.length];
  contacts.push({
    id: `contact-${index}`,
    lpId: lp.id,
    name: `${['李', '王', '张', '刘', '陈'][index % 5]}${['明', '芳', '伟', '洋', '晨'][index % 5]}`,
    title: '投资经理',
    department: '投资部',
    role: ['项目负责人', '执行联系人', '核心影响人'][index % 3],
    phone: `138-0000-${String(8100 + index)}`,
    email: `contact${index}@example.com`,
    owner: lp.owner,
    relationStrength: ['强', '中', '弱'][
      index % 3
    ] as Contact['relationStrength'],
    lastContactAt: lp.lastContactAt,
  });
}

const opportunities: FundraisingOpportunity[] = [
  [
    'opp-ningbo-growth',
    '宁波产业引导基金认缴智造成长基金一期',
    'lp-ningbo',
    'fund-growth-1',
    '张伟',
    ['contact-liuhaitao'],
    '签约认缴',
    5000,
    85,
    '完成协议签署并推进首笔实缴',
    '2025-06-04',
    '低风险',
  ],
  [
    'opp-huadong-industry',
    '华东产业资本参与先进制造基金二期',
    'lp-huadong',
    'fund-industry-2',
    '李明',
    ['contact-chenxiaodong'],
    '尽调评审',
    15000,
    70,
    '安排尽调会',
    '2025-06-03',
    '中风险',
  ],
  [
    'opp-shanghai-tech',
    '上海国资平台认缴科创成果转化基金',
    'lp-shanghai',
    'fund-tech-3',
    '周洋',
    ['contact-liuyang'],
    '内部决策',
    25000,
    50,
    '跟进内部决策进度',
    '2025-06-02',
    '中风险',
  ],
  [
    'opp-suzhou-growth',
    '苏州工业园区引导基金认缴智能制造基金',
    'lp-suzhou',
    'fund-growth-1',
    '张三',
    ['contact-zhaoqiang'],
    '签约认缴',
    18000,
    100,
    '签署合作协议',
    '2025-05-30',
    '低风险',
  ],
  [
    'opp-shenzhen-tech',
    '深圳市天使母基金参与早期科技基金',
    'lp-shenzhen',
    'fund-tech-3',
    '李明',
    ['contact-sunwei'],
    '实缴推进',
    12000,
    80,
    '推进首期实缴',
    '2025-05-28',
    '低风险',
  ],
  [
    'opp-hefei-material',
    '合肥高新区产业基金认缴新经济基金',
    'lp-hefei',
    'fund-material',
    '赵敏',
    ['contact-xuxuan'],
    '初步接洽',
    10000,
    20,
    '介绍基金情况',
    '2025-05-27',
    '低风险',
  ],
].map((values) => {
  const [
    id,
    name,
    lpId,
    fundId,
    owner,
    contactIds,
    stage,
    expectedAmount,
    probability,
    nextAction,
    lastFollowUpAt,
    risk,
  ] = values as [
    string,
    string,
    string,
    string,
    string,
    string[],
    OpportunityStage,
    number,
    number,
    string,
    string,
    FundraisingOpportunity['risk'],
  ];
  return {
    id,
    name,
    lpId,
    fundId,
    owner,
    contactIds,
    stage,
    expectedAmount,
    probability,
    nextAction,
    lastFollowUpAt,
    risk,
    source: '合作伙伴引荐',
    expectedDecisionAt: '2025-06-30',
    background: 'LP投资方向与基金策略高度匹配，具备长期合作基础。',
    judgement: '整体匹配度高，决策链清晰，建议按计划持续推进。',
    risks: ['宏观经济波动可能影响决策节奏', '内部审批时间存在不确定性'],
  };
});

const stages: OpportunityStage[] = [
  '潜在目标',
  '初步接洽',
  '方案沟通',
  '尽调评审',
  '内部决策',
  '签约认缴',
  '实缴推进',
];
for (let index = opportunities.length + 1; index <= 35; index += 1) {
  const lp = lps[(index + 2) % lps.length];
  const fund = funds[index % funds.length];
  opportunities.push({
    id: `opp-${index}`,
    name: `${lp.name}参与${fund.name}`,
    lpId: lp.id,
    fundId: fund.id,
    owner: lp.owner,
    contactIds: [lp.contactId],
    stage: stages[index % stages.length],
    expectedAmount: 1000 + index * 300,
    probability: 20 + (index % 8) * 10,
    nextAction: ['发送基金材料', '安排沟通会议', '补充返投方案'][index % 3],
    lastFollowUpAt: `2025-05-${String((index % 25) + 1).padStart(2, '0')}`,
    risk: index % 7 === 0 ? '高风险' : index % 3 === 0 ? '中风险' : '低风险',
    source: '行业活动',
    expectedDecisionAt: '2025-07-31',
    background: '通过行业活动建立联系，双方投资方向存在交集。',
    judgement: '保持跟进并进一步确认出资偏好。',
    risks: ['决策周期可能较长'],
  });
}

const commitments: Commitment[] = [
  {
    id: 'commitment-ningbo-growth',
    opportunityId: 'opp-ningbo-growth',
    fundId: 'fund-growth-1',
    lpId: 'lp-ningbo',
    amount: 5000,
    signedAt: '2025-05-28',
  },
  {
    id: 'commitment-suzhou-growth',
    opportunityId: 'opp-suzhou-growth',
    fundId: 'fund-growth-1',
    lpId: 'lp-suzhou',
    amount: 18000,
    signedAt: '2025-03-31',
  },
  {
    id: 'commitment-growth-3',
    opportunityId: 'history-growth-3',
    fundId: 'fund-growth-1',
    lpId: 'lp-jinmu',
    amount: 3000,
    signedAt: '2025-02-18',
  },
  {
    id: 'commitment-growth-4',
    opportunityId: 'history-growth-4',
    fundId: 'fund-growth-1',
    lpId: 'lp-listed',
    amount: 22000,
    signedAt: '2024-12-18',
  },
  {
    id: 'commitment-growth-5',
    opportunityId: 'history-growth-5',
    fundId: 'fund-growth-1',
    lpId: 'lp-triangle',
    amount: 24000,
    signedAt: '2024-10-08',
  },
  {
    id: 'commitment-industry-1',
    opportunityId: 'history-industry-1',
    fundId: 'fund-industry-2',
    lpId: 'lp-huadong',
    amount: 108000,
    signedAt: '2024-10-01',
  },
  {
    id: 'commitment-tech-1',
    opportunityId: 'history-tech-1',
    fundId: 'fund-tech-3',
    lpId: 'lp-shanghai',
    amount: 54000,
    signedAt: '2024-05-20',
  },
  {
    id: 'commitment-shenzhen-tech',
    opportunityId: 'opp-shenzhen-tech',
    fundId: 'fund-tech-3',
    lpId: 'lp-shenzhen',
    amount: 12000,
    signedAt: '2025-05-16',
  },
  {
    id: 'commitment-software-1',
    opportunityId: 'history-software-1',
    fundId: 'fund-software',
    lpId: 'lp-listed',
    amount: 52000,
    signedAt: '2023-12-01',
  },
  {
    id: 'commitment-material-1',
    opportunityId: 'history-material-1',
    fundId: 'fund-material',
    lpId: 'lp-hefei',
    amount: 68500,
    signedAt: '2025-04-16',
  },
  {
    id: 'commitment-robot-1',
    opportunityId: 'history-robot-1',
    fundId: 'fund-robot',
    lpId: 'lp-jinmu',
    amount: 120000,
    signedAt: '2023-01-18',
  },
];

const makePlan = (commitment: Commitment, paid: number): ContributionPlan => ({
  id:
    commitment.id === 'commitment-ningbo-growth'
      ? 'plan-ningbo-growth'
      : `plan-${commitment.id}`,
  commitmentId: commitment.id,
  fundId: commitment.fundId,
  lpId: commitment.lpId,
  owner: lps.find((lp) => lp.id === commitment.lpId)?.owner ?? '张伟',
  installments:
    commitment.id === 'commitment-ningbo-growth'
      ? [
          {
            id: 'installment-ningbo-1',
            name: '第一期',
            dueAmount: 2500,
            dueAt: '2025-05-20',
            paidAmount: 2500,
            paidAt: '2025-05-20',
            status: '已到账',
          },
          {
            id: 'installment-ningbo-2',
            name: '第二期',
            dueAmount: 1500,
            dueAt: '2025-06-30',
            paidAmount: 0,
            status: '待缴',
          },
          {
            id: 'installment-ningbo-3',
            name: '第三期',
            dueAmount: 1000,
            dueAt: '根据项目进度通知',
            paidAmount: 0,
            status: '待通知',
          },
        ]
      : [
          {
            id: `installment-${commitment.id}-1`,
            name: '第一期',
            dueAmount: commitment.amount,
            dueAt: '2025-06-20',
            paidAmount: paid,
            paidAt: paid ? '2025-05-20' : undefined,
            status:
              paid >= commitment.amount
                ? '已到账'
                : paid > 0
                  ? '部分到账'
                  : '待缴',
          },
        ],
});

const paidByCommitment: Record<string, number> = {
  'commitment-suzhou-growth': 12000,
  'commitment-growth-3': 3000,
  'commitment-growth-4': 14000,
  'commitment-growth-5': 16500,
  'commitment-industry-1': 63000,
  'commitment-tech-1': 32000,
  'commitment-shenzhen-tech': 4000,
  'commitment-software-1': 28000,
  'commitment-material-1': 37500,
  'commitment-robot-1': 77000,
};
const contributionPlans = commitments.map((item) =>
  makePlan(
    item,
    paidByCommitment[item.id] ??
      (item.id === 'commitment-ningbo-growth' ? 2500 : 0),
  ),
);

const tasks: Task[] = [
  {
    id: 'task-1',
    title: '宁波产业引导基金－第三次出资审批',
    fundId: 'fund-growth-1',
    lpId: 'lp-ningbo',
    opportunityId: 'opp-ningbo-growth',
    owner: '张伟',
    dueAt: '2025-06-05',
    priority: '高',
    status: '待处理',
  },
  {
    id: 'task-2',
    title: 'LP尽调材料补充',
    fundId: 'fund-growth-1',
    lpId: 'lp-huadong',
    opportunityId: 'opp-huadong-industry',
    owner: '李明',
    dueAt: '2025-06-06',
    priority: '中',
    status: '进行中',
  },
  {
    id: 'task-3',
    title: '投资材料更新',
    fundId: 'fund-material',
    lpId: 'lp-jinmu',
    owner: '周舟',
    dueAt: '2025-06-07',
    priority: '中',
    status: '待处理',
  },
  {
    id: 'task-4',
    title: '资质信息更新',
    lpId: 'lp-ningbo',
    owner: '张伟',
    dueAt: '2025-06-08',
    priority: '低',
    status: '待处理',
  },
  {
    id: 'task-5',
    title: '年度报告签署',
    fundId: 'fund-growth-1',
    lpId: 'lp-huadong',
    owner: '李明',
    dueAt: '2025-06-09',
    priority: '低',
    status: '待处理',
  },
];
for (let index = 6; index <= 50; index += 1)
  tasks.push({
    id: `task-${index}`,
    title: [
      '发送基金介绍材料',
      '补充历史业绩',
      '安排管理合伙人见面',
      '回复LP尽调问题',
    ][index % 4],
    fundId: funds[index % funds.length].id,
    lpId: lps[index % lps.length].id,
    opportunityId: opportunities[index % opportunities.length].id,
    owner: ['李明', '王芳', '张伟'][index % 3],
    dueAt: `2025-06-${String((index % 25) + 1).padStart(2, '0')}`,
    priority: ['高', '中', '低'][index % 3] as Task['priority'],
    status: index % 5 === 0 ? '已完成' : index % 2 === 0 ? '进行中' : '待处理',
  });

const followUps: FollowUp[] = [];
for (let index = 1; index <= 100; index += 1) {
  const opp = opportunities[index % opportunities.length];
  followUps.push({
    id: `follow-${index}`,
    lpId: opp.lpId,
    opportunityId: opp.id,
    contactId: opp.contactIds[0],
    type: ['电话', '拜访', '会议', '文件'][index % 4],
    content: [
      '沟通基金最新募资情况，确认下一次沟通时间',
      '提交了项目资料与基金介绍文件',
      '围绕返投要求和决策流程进行深入沟通',
    ][index % 3],
    author: opp.owner,
    createdAt: `2025-${index % 3 === 0 ? '04' : '05'}-${String((index % 27) + 1).padStart(2, '0')} ${String(9 + (index % 8)).padStart(2, '0')}:30`,
    nextAction: opp.nextAction,
  });
}

const dueDiligenceQuestions: DueDiligenceQuestion[] = [];
for (let index = 1; index <= 40; index += 1) {
  const opp = opportunities[index % opportunities.length];
  dueDiligenceQuestions.push({
    id: `dd-${index}`,
    opportunityId: opp.id,
    category: ['GP及团队', '历史业绩', '基金策略', '返投安排'][index % 4],
    question: [
      '请补充核心团队历史投资案例',
      '请说明基金返投认定口径',
      '请提供近三年退出业绩明细',
    ][index % 3],
    owner: opp.owner,
    status: ['待回复', '准备中', '已回复', '已确认'][
      index % 4
    ] as DueDiligenceQuestion['status'],
    answer: index % 4 > 1 ? '相关材料已整理并提交。' : undefined,
  });
}

const activities: Activity[] = [
  {
    id: 'activity-1',
    type: '新增LP',
    title: '新增LP：华东产业资本',
    description: '已完成LP信息录入',
    createdAt: '2025-06-03 09:32',
    entityType: 'lp',
    entityId: 'lp-huadong',
  },
  {
    id: 'activity-2',
    type: '新增募资机会',
    title: '新增募资机会',
    description: '先进制造产业基金',
    createdAt: '2025-06-03 08:45',
    entityType: 'opportunity',
    entityId: 'opp-huadong-industry',
  },
  {
    id: 'activity-3',
    type: '确认认缴',
    title: '确认认缴',
    description: '华东产业资本认缴智造成长基金一期 3.50 亿元',
    createdAt: '2025-06-02 16:20',
    entityType: 'fund',
    entityId: 'fund-growth-1',
  },
  {
    id: 'activity-4',
    type: '实缴到账',
    title: '第一笔实缴到账',
    description: '宁波市国资委实缴到账 2.00 亿元',
    createdAt: '2025-06-02 15:05',
    entityType: 'contribution',
    entityId: 'plan-ningbo-growth',
  },
];

export const createInitialState = (): FundraisingDomainState => ({
  version: 1,
  funds: toRecord(funds),
  lps: toRecord(lps),
  contacts: toRecord(contacts),
  opportunities: toRecord(opportunities),
  commitments: toRecord(commitments),
  contributionPlans: toRecord(contributionPlans),
  followUps: toRecord(followUps),
  tasks: toRecord(tasks),
  dueDiligenceQuestions: toRecord(dueDiligenceQuestions),
  activities: toRecord(activities),
});

/** 运行时初始态：空字典，数据只来自 BFF hydrate / seed-demo。 */
export const createEmptyFundraisingState = (): FundraisingDomainState => ({
  version: 1,
  funds: {},
  lps: {},
  contacts: {},
  opportunities: {},
  commitments: {},
  contributionPlans: {},
  followUps: {},
  tasks: {},
  dueDiligenceQuestions: {},
  activities: {},
});

export const selectFundMetrics = (
  state: FundraisingDomainState,
  fundId: string,
) => {
  const fund = state.funds[fundId];
  const fundCommitments = Object.values(state.commitments).filter(
    (item) => item.fundId === fundId,
  );
  const committed = fundCommitments.reduce((sum, item) => sum + item.amount, 0);
  const paid = Object.values(state.contributionPlans)
    .filter((item) => item.fundId === fundId)
    .flatMap((item) => item.installments)
    .reduce((sum, item) => sum + item.paidAmount, 0);
  return {
    target: fund?.target ?? 0,
    committed,
    paid,
    outstanding: Math.max(0, committed - paid),
    completionRate:
      fund && fund.target > 0 ? (committed / fund.target) * 100 : null,
    lpCount: new Set(fundCommitments.map((item) => item.lpId)).size,
  };
};

export const selectLpMetrics = (
  state: FundraisingDomainState,
  lpId: string,
) => {
  const commitmentsForLp = Object.values(state.commitments).filter(
    (item) => item.lpId === lpId,
  );
  const committed = commitmentsForLp.reduce(
    (sum, item) => sum + item.amount,
    0,
  );
  const paid = Object.values(state.contributionPlans)
    .filter((item) => item.lpId === lpId)
    .flatMap((item) => item.installments)
    .reduce((sum, item) => sum + item.paidAmount, 0);
  return {
    committed,
    paid,
    outstanding: Math.max(0, committed - paid),
    opportunityCount: Object.values(state.opportunities).filter(
      (item) => item.lpId === lpId && !['完成', '失败'].includes(item.stage),
    ).length,
  };
};

export const fundraisingReducer = (
  state: FundraisingDomainState,
  command: FundraisingCommand,
): FundraisingDomainState => {
  switch (command.type) {
    case 'reset':
      return createEmptyFundraisingState();
    case 'hydrateFunds': {
      // 部署环境偶发拿到包装对象而非数组；非数组按空列表处理，避免 forEach 炸掉整页。
      const serverList = Array.isArray(command.payload) ? command.payload : [];
      if (serverList.length === 0) {
        return { ...state, funds: {} };
      }
      const funds: Record<string, Fund> = {};
      const legacyIdToUuid: Record<string, string> = {};
      serverList.forEach((record) => {
        const fund = toDomainFund(record);
        funds[fund.id] = fund;
        legacyIdToUuid[record.code.toLowerCase()] = fund.id;
      });
      // 兼容历史引用：机会/认缴/出资计划里旧基金 id（如 fund-growth-1）按 code 映射到 uuid。
      function mapFundRef(ref: string): string;
      function mapFundRef(ref: string | undefined): string | undefined;
      function mapFundRef(ref: string | undefined) {
        return ref && legacyIdToUuid[ref] ? legacyIdToUuid[ref] : ref;
      }
      const rewriteFundRef = <T extends { fundId: string }>(item: T): T =>
        item.fundId ? { ...item, fundId: mapFundRef(item.fundId) } : item;
      return {
        ...state,
        funds,
        opportunities: mapValues(state.opportunities, rewriteFundRef),
        commitments: mapValues(state.commitments, rewriteFundRef),
        contributionPlans: mapValues(state.contributionPlans, rewriteFundRef),
        tasks: mapValues(state.tasks, (task) =>
          task.fundId ? { ...task, fundId: mapFundRef(task.fundId) } : task,
        ),
        activities: mapValues(state.activities, (activity) =>
          activity.entityType === 'fund'
            ? { ...activity, entityId: mapFundRef(activity.entityId) }
            : activity,
        ),
      };
    }
    case 'hydrateCrm': {
      const { lps, contacts, opportunities } = command.payload;
      return {
        ...state,
        lps,
        contacts,
        opportunities,
      };
    }
    case 'hydrateContributions': {
      const { commitments, contributionPlans } = command.payload;
      return {
        ...state,
        commitments,
        contributionPlans,
      };
    }
    case 'createFund':
      return {
        ...state,
        funds: { ...state.funds, [command.payload.id]: command.payload },
      };
    case 'updateFund':
      return {
        ...state,
        funds: {
          ...state.funds,
          [command.payload.id]: {
            ...state.funds[command.payload.id],
            ...command.payload,
          },
        },
      };
    case 'createLp':
      return {
        ...state,
        lps: { ...state.lps, [command.payload.id]: command.payload },
      };
    case 'updateLp':
      return {
        ...state,
        lps: {
          ...state.lps,
          [command.payload.id]: {
            ...state.lps[command.payload.id],
            ...command.payload,
          },
        },
      };
    case 'createContact':
      return {
        ...state,
        contacts: { ...state.contacts, [command.payload.id]: command.payload },
      };
    case 'createOpportunity':
      return {
        ...state,
        opportunities: {
          ...state.opportunities,
          [command.payload.id]: command.payload,
        },
      };
    case 'updateOpportunityStage':
      return {
        ...state,
        opportunities: {
          ...state.opportunities,
          [command.payload.id]: {
            ...state.opportunities[command.payload.id],
            stage: command.payload.stage,
          },
        },
      };
    case 'createFollowUp':
      return {
        ...state,
        followUps: {
          ...state.followUps,
          [command.payload.id]: command.payload,
        },
      };
    case 'createTask':
      return {
        ...state,
        tasks: { ...state.tasks, [command.payload.id]: command.payload },
      };
    case 'completeTask':
      return {
        ...state,
        tasks: {
          ...state.tasks,
          [command.payload.id]: {
            ...state.tasks[command.payload.id],
            status: '已完成',
          },
        },
      };
    case 'recordCommitment': {
      const existing = Object.values(state.commitments).find(
        (item) => item.opportunityId === command.payload.opportunityId,
      );
      const commitment: Commitment = command.payload;
      const priorPlan = existing
        ? Object.values(state.contributionPlans).find(
            (item) => item.commitmentId === existing.id,
          )
        : undefined;
      const planId = `plan-${commitment.id}`;
      const currentPlan = priorPlan;
      const plan: ContributionPlan = currentPlan
        ? {
            ...currentPlan,
            installments: currentPlan.installments.map((item, index) =>
              index === currentPlan.installments.length - 1
                ? {
                    ...item,
                    dueAmount: Math.max(
                      item.paidAmount,
                      commitment.amount -
                        currentPlan.installments
                          .slice(0, -1)
                          .reduce((sum, row) => sum + row.dueAmount, 0),
                    ),
                  }
                : item,
            ),
          }
        : {
            id: planId,
            commitmentId: commitment.id,
            fundId: commitment.fundId,
            lpId: commitment.lpId,
            owner:
              state.opportunities[commitment.opportunityId]?.owner ?? '张伟',
            installments: [
              {
                id: `installment-${commitment.id}-1`,
                name: '待配置计划',
                dueAmount: commitment.amount,
                dueAt: '待配置',
                paidAmount: 0,
                status: '待通知',
              },
            ],
          };
      const nextCommitments = { ...state.commitments };
      const nextPlans = { ...state.contributionPlans };
      if (existing) delete nextCommitments[existing.id];
      if (priorPlan) delete nextPlans[priorPlan.id];
      const normalizedPlan = {
        ...plan,
        id: planId,
        commitmentId: commitment.id,
      };
      return {
        ...state,
        commitments: { ...nextCommitments, [commitment.id]: commitment },
        contributionPlans: {
          ...nextPlans,
          [normalizedPlan.id]: normalizedPlan,
        },
        opportunities: {
          ...state.opportunities,
          [commitment.opportunityId]: {
            ...state.opportunities[commitment.opportunityId],
            stage: '签约认缴',
          },
        },
      };
    }
    case 'recordContributionReceipt': {
      const plan = state.contributionPlans[command.payload.planId];
      if (!plan) return state;
      return {
        ...state,
        contributionPlans: {
          ...state.contributionPlans,
          [plan.id]: {
            ...plan,
            installments: plan.installments.map((item) =>
              item.id === command.payload.installmentId
                ? {
                    ...item,
                    paidAmount: command.payload.amount,
                    paidAt: command.payload.paidAt,
                    status:
                      command.payload.amount >= item.dueAmount
                        ? '已到账'
                        : '部分到账',
                  }
                : item,
            ),
          },
        },
      };
    }
    default:
      return state;
  }
};

export const formatMoney = (
  amount: number | null | undefined,
  compact = true,
) => {
  if (amount == null) return '—';
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  if (compact && Math.abs(value) >= 10000)
    return `${(value / 10000).toFixed(value % 10000 === 0 ? 1 : 2)} 亿元`;
  return `${value.toLocaleString('zh-CN')} 万元`;
};
